import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import TableSection from './TableSection';
import { formatDate } from '../../utils/formatDate';
import { AdStatus, adStatusTranslations } from '../../constants/ad-constants';
import useResize from '../../hooks/useResize';
import { useNavigate } from 'react-router-dom';
import { changeAdStatus, deleteAd } from '../../api/ad-api';
import { message } from 'antd';
import DeleteModal from '../modals/DeleteModal';

const archivedFilterSection = [
  {
    buttonLabel: 'Arhivirani',
    title: 'Arhivirani',
    type: 'adStatus',
    items: [
      { key: AdStatus.ARCHIVED_AUTO, label: 'Automatski' },
      { key: AdStatus.ARCHIVED_MANUAL, label: 'Admin' },
      { key: AdStatus.ARCHIVED_USER, label: 'Korisnik' },
    ],
  },
];

const deactivatedFilterSection = [
  {
    buttonLabel: 'Neaktivni',
    title: 'Neaktivni',
    type: 'adStatus',
    items: [
      { key: AdStatus.DEACTIVATED_USER, label: 'Deaktiviran od korisnika' },
      { key: AdStatus.DEACTIVATED_ADMIN, label: 'Deaktiviran od admina' },
    ],
  },
];

const normalizeCarManufacturer = (name) => {
  return name.replaceAll('_', ' ');
};

const AdsTable = ({
  handleSearch,
  filter,
  handleFilterChange,
  ads,
  currentPage,
  onPageChange,
  totalPages,
  activeTab = 'svi',
  setError,
  setMessage,
  refetchAds,
  showAddButton = true,
}) => {
  const width = useResize();
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [adToDelete, setAdToDelete] = useState(null);

  const getAdStatus = (ad) => {
    if (activeTab === 'arhivirani') return ad.adStatus;
    if (ad.sold) return AdStatus.SOLD;
    return ad.adStatus;
  };

  const getSectionsForTab = (tab) => {
    switch (tab) {
      case 'neaktivni':
        return [deactivatedFilterSection];
      case 'arhivirani':
        return [archivedFilterSection];
      default:
        return [];
    }
  };

  const handleArchive = async (status, adId) => {
    if (!status) return;

    if (
      status === AdStatus.ACTIVATED ||
      status === AdStatus.DEACTIVATED_USER ||
      status === AdStatus.DEACTIVATED_ADMIN
    ) {
      const { error } = await changeAdStatus(AdStatus.ARCHIVED_MANUAL, adId);

      if (error) {
        setError(error);
        return;
      }
      await refetchAds();
      message.success('Oglas je uspešno arhiviran.', 5);
    }
  };

  const handleActivate = async (status, adId) => {
    if (!status) return;

    if (
      status === AdStatus.DEACTIVATED_USER ||
      status === AdStatus.DEACTIVATED_ADMIN ||
      status === AdStatus.ARCHIVED_MANUAL ||
      status === AdStatus.ARCHIVED_USER
    ) {
      const { error } = await changeAdStatus(AdStatus.ACTIVATED, adId);

      if (error) {
        setError(error);
        return;
      }
      await refetchAds();
      message.success('Oglas je uspešno aktiviran.', 5);
    }
  };

  const handleDeactivate = async (status, adId) => {
    if (!status) return;

    if (status === AdStatus.ACTIVATED || status === AdStatus.DEACTIVATED_USER) {
      const { error } = await changeAdStatus(AdStatus.DEACTIVATED_ADMIN, adId);

      if (error) {
        setError(error);
        return;
      }
      await refetchAds();
      message.success('Oglas je uspešno deaktiviran.', 5);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    const { message, error } = await deleteAd(adToDelete);

    if (error) {
      setMessage(error);
    } else {
      setMessage(message || 'Oglas obrisan');
      await refetchAds();
      setLoading(false);
      setDone(true);

      setTimeout(() => {
        setIsDeleteModalOpen(false);
      }, 1000);
    }
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
  };

  const columns = useMemo(() => {
    return width <= 768
      ? [
          {
            key: 'title',
            label: 'Naziv oglasa',
            render: (_, row) =>
              `${normalizeCarManufacturer(row.carManufacturer)} ${row.carModel} ${row.engineVersion}`,
          },
        ]
      : [
          {
            key: 'title',
            label: 'Naziv oglasa',
            render: (_, row) =>
              `${normalizeCarManufacturer(row.carManufacturer)} ${row.carModel} ${row.engineVersion}`,
          },
          {
            key: 'status',
            label: 'Status',
            render: (_, row) => {
              const statusKey = getAdStatus(row);
              const className = statusKey.replace(/_.*$/, '').toLowerCase();
              return (
                <span className={className}>
                  {adStatusTranslations[statusKey]}
                </span>
              );
            },
          },
          {
            key: 'createdAt',
            label: 'Datum oglasa',
            render: (val) =>
              formatDate(val, { month: 'numeric' }).replace(/\s/g, ''),
          },
          {
            key: 'price',
            label: 'Cena',
            render: (val, row) =>
              val > 0 && !row.priceNegotiable
                ? `${val}${row.currency === 'EUR' ? '€' : ' rsd'}`
                : 'Po dogovoru',
          },
        ];
    // eslint-disable-next-line
  }, [width]);

  return (
    <>
      <TableSection
        key={activeTab}
        onSearch={handleSearch}
        createButtonDisplay="Dodaj oglas"
        onCreateClick={() => navigate('/oglas/novi')}
        showFilter={!['svi', 'aktivni', 'prodani'].includes(activeTab)}
        filter={{
          sections: getSectionsForTab(activeTab),
          selected: filter,
        }}
        onFilterChange={handleFilterChange}
        data={ads}
        columns={columns}
        actions={[
          {
            key: 'view',
            label: 'Pregledaj oglas',
            onClick: (ad) => navigate(`/oglas/${ad._id}`),
          },
          {
            key: 'edit',
            label: 'Izmeni oglas',
            onClick: (ad) => navigate(`/oglas/${ad._id}?editMode=true`),
          },
          {
            key: 'activate',
            label: 'Aktiviraj',
            visible: (ad) =>
              [
                AdStatus.ARCHIVED_MANUAL,
                AdStatus.DEACTIVATED_USER,
                AdStatus.DEACTIVATED_ADMIN,
                AdStatus.ARCHIVED_USER,
              ].includes(ad.adStatus),
            onClick: (ad) => handleActivate(ad.adStatus, ad._id),
          },
          {
            key: 'deactivate',
            label: 'Deaktiviraj',
            visible: (ad) =>
              [AdStatus.ACTIVATED, AdStatus.DEACTIVATED_USER].includes(
                ad.adStatus
              ),
            onClick: (ad) => handleDeactivate(ad.adStatus, ad._id),
          },
          {
            key: 'archive',
            label: 'Arhiviraj',
            visible: (ad) =>
              [
                AdStatus.ACTIVATED,
                AdStatus.DEACTIVATED_USER,
                AdStatus.DEACTIVATED_ADMIN,
              ].includes(ad.adStatus),
            onClick: (ad) => handleArchive(ad.adStatus, ad._id),
          },
          {
            key: 'delete',
            label: 'Obriši oglas',
            danger: true,
            className: 'delete',
            icon: <img src="/assets/icons/delete.svg" alt="Delete" />,
            onClick: (ad) => {
              setAdToDelete(ad._id);
              setIsDeleteModalOpen(true);
            },
          },
        ]}
        emptyState={{
          icon: '/assets/icons/no-users.svg',
          title: 'Trenutno nema oglasa',
          description: 'Dodajte novi oglas da bi se prikazao ovde.',
        }}
        showPagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={onPageChange}
        searchPlaceholder="Pretraži oglase"
        showCreateButton={showAddButton}
      />
      <DeleteModal
        visible={isDeleteModalOpen}
        onDelete={handleDelete}
        onCancel={handleDeleteModalClose}
        loading={loading}
        done={done}
        title="Brisanje oglasa"
        message="Da li ste sigurni da želite da obrišete ovaj oglas?"
        buttonText="Obriši oglas"
      />
    </>
  );
};

AdsTable.propTypes = {
  ads: PropTypes.object.isRequired,
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  filter: PropTypes.object.isRequired,
  handleSearch: PropTypes.func.isRequired,
  handleFilterChange: PropTypes.func.isRequired,
  activeTab: PropTypes.string,
  setError: PropTypes.func,
  setMessage: PropTypes.func,
  refetchAds: PropTypes.func,
  showAddButton: PropTypes.bool,
};

export default AdsTable;
